/** @jsxImportSource @emotion/react */
// React/core
import React, { useState, useCallback, useMemo } from 'react'
// Third-party
import {
  DeleteOutlined,
  LoadingOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import { css } from '@emotion/react'
import { Select, Spin } from 'antd'
// Internal (@sportninja/common)
import colors from '@sportninja/common/constants/appColors'
// Relative
import { ChevronDown, ChevronRight } from './icons'
import { iconButtonStyles } from '../styles/shared'
import type { DivisionItem } from '../hooks/useSchedulerDivisions'
import { AddDivisionModal } from './AddDivisionModal'
import { AddTeamModal } from './AddTeamModal'

interface DivisionTeamsSectionProps {
  divisions: DivisionItem[]
  loading?: boolean
  error?: Error | null
  onToggleExpanded?: (divisionId: string) => void
  onAddDivision?: (divisionName: string) => Promise<void>
  onAddTeam?: (divisionId: string, teamName: string) => Promise<void>
  onDeleteTeam?: (divisionId: string, teamId: string, teamName: string) => void
  onDeleteDivision?: (divisionId: string, divisionName: string) => void
}

export const DivisionTeamsSection: React.FC<DivisionTeamsSectionProps> = ({
  divisions,
  loading = false,
  error = null,
  onToggleExpanded,
  onAddDivision,
  onAddTeam,
  onDeleteTeam,
  onDeleteDivision,
}) => {
  // Filter state
  const [divisionFilter, setDivisionFilter] = useState<string>('all')
  const [teamFilter, setTeamFilter] = useState<string>('all')

  // Modal state
  const [isAddDivisionModalOpen, setIsAddDivisionModalOpen] = useState(false)
  const [isAddTeamModalOpen, setIsAddTeamModalOpen] = useState(false)
  const [addTeamDivisionId, setAddTeamDivisionId] = useState<string>('')
  const [addTeamDivisionName, setAddTeamDivisionName] = useState<string>('')

  // Division filter options
  const divisionOptions = useMemo(() => {
    const options = [{ label: 'All Divisions', value: 'all' }]
    divisions.forEach((div) => {
      options.push({ label: div.name, value: div.id })
    })
    return options
  }, [divisions])

  // Team filter options
  const teamOptions = useMemo(() => {
    const options = [{ label: 'All Teams', value: 'all' }]
    divisions.forEach((div) => {
      div.teams.forEach((team) => {
        if (!options.find((o) => o.value === team.id)) {
          options.push({ label: team.name, value: team.id })
        }
      })
    })
    return options
  }, [divisions])

  // Filtered divisions
  const filteredDivisions = useMemo(() => {
    let result = divisions
    if (divisionFilter !== 'all') {
      result = result.filter((d) => d.id === divisionFilter)
    }
    if (teamFilter !== 'all') {
      result = result.filter((d) => d.teams.some((t) => t.id === teamFilter))
    }
    return result
  }, [divisions, divisionFilter, teamFilter])

  const handleAddDivisionClick = useCallback(() => {
    setIsAddDivisionModalOpen(true)
  }, [])

  const handleAddTeamClick = useCallback(
    (divisionId: string, divisionName: string) => {
      setAddTeamDivisionId(divisionId)
      setAddTeamDivisionName(divisionName)
      setIsAddTeamModalOpen(true)
    },
    []
  )

  // Select dropdown styles
  const selectStyles = css`
    width: 100%;

    .ant-select-selector {
      background-color: ${colors.SECONDARY_400} !important;
      border: 1px solid ${colors.SECONDARY_200} !important;
      border-radius: 8px !important;
      color: ${colors.WHITE} !important;
      padding: 4px 16px !important;
      height: auto !important;
      min-height: 40px !important;
    }

    .ant-select-selection-item {
      color: ${colors.WHITE} !important;
      font-family: 'Inter', sans-serif;
      font-size: 14px;
      font-weight: 400;
      line-height: 20.3px;
    }

    .ant-select-arrow {
      color: ${colors.WHITE} !important;
    }

    &:hover .ant-select-selector {
      border-color: ${colors.PRIMARY_300} !important;
    }
  `

  return (
    <div>
      {/* Filter Bar */}
      <div
        css={css`
          display: flex;
          gap: 16px;
          align-items: flex-end;
          margin-bottom: 24px;
        `}
      >
        {/* Division Filter */}
        <div
          css={css`
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 4px;
          `}
        >
          <span
            css={css`
              font-family: 'Inter', sans-serif;
              font-size: 14px;
              font-weight: 400;
              line-height: 20.3px;
              color: ${colors.WHITE};
            `}
          >
            Division
          </span>
          <Select
            value={divisionFilter}
            onChange={setDivisionFilter}
            css={selectStyles}
            popupClassName='scheduler-dark-dropdown'
            options={divisionOptions}
          />
        </div>

        {/* Team Filter */}
        <div
          css={css`
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 4px;
          `}
        >
          <span
            css={css`
              font-family: 'Inter', sans-serif;
              font-size: 14px;
              font-weight: 400;
              line-height: 20.3px;
              color: ${colors.WHITE};
            `}
          >
            Team
          </span>
          <Select
            value={teamFilter}
            onChange={setTeamFilter}
            css={selectStyles}
            popupClassName='scheduler-dark-dropdown'
            options={teamOptions}
          />
        </div>
      </div>

      {/* Section Header: DIVISION & TEAMS */}
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 16px;
          justify-content: center;
          margin-bottom: 16px;
        `}
      >
        <h2
          css={css`
            flex: 1;
            margin: 0;
            font-family: 'Inter', sans-serif;
            font-size: 14px;
            font-weight: 600;
            line-height: 16.8px;
            letter-spacing: 1.68px;
            text-transform: uppercase;
            color: ${colors.WHITE};
          `}
        >
          DIVISION & TEAMS
        </h2>
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 8px;
          `}
        >
          {/* Add Button */}
          <button
            type='button'
            onClick={handleAddDivisionClick}
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              padding: 4px 8px;
              background: transparent;
              border: 1px solid ${colors.PRIMARY_300};
              border-radius: 8px;
              cursor: pointer;
              transition: all 0.2s ease;

              &:hover {
                background: ${colors.PRIMARY_300}20;
              }
            `}
          >
            <PlusOutlined
              css={css`
                font-size: 16px;
                color: ${colors.PRIMARY_300};
              `}
            />
            <span
              css={css`
                font-family: 'Inter', sans-serif;
                font-size: 14px;
                font-weight: 500;
                line-height: 20.3px;
                color: ${colors.PRIMARY_300};
              `}
            >
              Add
            </span>
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 48px;
            background-color: ${colors.SECONDARY_300};
            border: 1px solid ${colors.SECONDARY_200};
            border-radius: 8px;
          `}
        >
          <Spin
            indicator={
              <LoadingOutlined
                style={{ fontSize: 32, color: colors.PRIMARY_300 }}
                spin
              />
            }
          />
          <span
            css={css`
              margin-left: 12px;
              color: ${colors.NEUTRAL_50};
              font-family: 'Inter', sans-serif;
              font-size: 14px;
            `}
          >
            Loading divisions...
          </span>
        </div>
      )}

      {/* Error State */}
      {!loading && error && (
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 32px;
            background-color: ${colors.SECONDARY_300};
            border: 1px solid ${colors.SECONDARY_200};
            border-radius: 8px;
            color: ${colors.NEUTRAL_50};
            font-family: 'Inter', sans-serif;
            font-size: 14px;
          `}
        >
          Failed to load divisions. Please try again.
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && filteredDivisions.length === 0 && (
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 48px;
            background-color: ${colors.SECONDARY_300};
            border: 1px solid ${colors.SECONDARY_200};
            border-radius: 8px;
            color: ${colors.NEUTRAL_50};
            font-family: 'Inter', sans-serif;
            font-size: 14px;
          `}
        >
          No divisions found for this competition.
        </div>
      )}

      {/* Divisions Accordion List */}
      {!loading && !error && filteredDivisions.length > 0 && (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 2px;
          `}
        >
          {filteredDivisions.map((division) => (
            <React.Fragment key={division.id}>
              {/* Division Row Header */}
              <DivisionRow
                division={division}
                isExpanded={division.isExpanded || false}
                onToggleExpand={() => onToggleExpanded?.(division.id)}
                onAddTeam={() => handleAddTeamClick(division.id, division.name)}
                onDelete={() => onDeleteDivision?.(division.id, division.name)}
              />

              {/* Expanded Content - Teams List */}
              {division.isExpanded && (
                <DivisionExpandedContent
                  division={division}
                  onDeleteTeam={onDeleteTeam}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Add Division Modal */}
      <AddDivisionModal
        isOpen={isAddDivisionModalOpen}
        onClose={() => setIsAddDivisionModalOpen(false)}
        onSubmit={async (name) => {
          await onAddDivision?.(name)
        }}
      />

      {/* Add Team Modal */}
      <AddTeamModal
        isOpen={isAddTeamModalOpen}
        onClose={() => setIsAddTeamModalOpen(false)}
        divisionName={addTeamDivisionName}
        onSubmit={async (name) => {
          await onAddTeam?.(addTeamDivisionId, name)
        }}
      />
    </div>
  )
}

// ============================================
// Division Row Component
// ============================================

interface DivisionRowProps {
  division: DivisionItem
  isExpanded: boolean
  onToggleExpand: () => void
  onAddTeam: () => void
  onDelete: () => void
}

const DivisionRow: React.FC<DivisionRowProps> = ({
  division,
  isExpanded,
  onToggleExpand,
  onAddTeam,
  onDelete,
}) => {
  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        gap: 16px;
        padding: 8px 16px;
        background-color: ${colors.SECONDARY_400};
        border-radius: 8px;
        cursor: pointer;
        user-select: none;

        &:hover {
          background-color: #1e2733;
        }
      `}
      onClick={onToggleExpand}
      role='button'
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onToggleExpand()
        }
      }}
    >
      {/* Expand/Collapse Icon */}
      <div
        css={css`
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        `}
      >
        {isExpanded ? <ChevronDown size={24} /> : <ChevronRight size={24} />}
      </div>

      {/* Division Name & Subtitle */}
      <div
        css={css`
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
          min-width: 0;
          overflow: hidden;
        `}
      >
        <span
          css={css`
            font-family: 'Inter', sans-serif;
            font-size: 16px;
            font-weight: 500;
            line-height: 23.2px;
            color: ${colors.WHITE};
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            opacity: ${division.isApproved ? 0.5 : 1};
          `}
        >
          {division.name}
        </span>
        <span
          css={css`
            font-family: 'Inter', sans-serif;
            font-size: 12px;
            font-weight: 400;
            line-height: 17.4px;
            color: ${colors.NEUTRAL_50};
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          `}
        >
          {division.lastUpdate || division.subtitle || 'Schedule Pending'}
        </span>
      </div>

      {/* Stats: Teams */}
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          justify-content: center;
          min-width: 80px;
          opacity: ${division.isApproved ? 0.5 : 1};
        `}
      >
        <span
          css={css`
            font-family: 'Inter', sans-serif;
            font-size: 12px;
            font-weight: 500;
            line-height: 17.4px;
            color: ${colors.NEUTRAL_70};
          `}
        >
          TEAMS
        </span>
        <span
          css={css`
            font-family: 'Inter', sans-serif;
            font-size: 14px;
            font-weight: 400;
            line-height: 20.3px;
            color: ${colors.WHITE};
          `}
        >
          {division.teamsCount}
        </span>
      </div>

      {/* Stats: Players */}
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          justify-content: center;
          min-width: 80px;
          opacity: ${division.isApproved ? 0.5 : 1};
        `}
      >
        <span
          css={css`
            font-family: 'Inter', sans-serif;
            font-size: 12px;
            font-weight: 500;
            line-height: 17.4px;
            color: ${colors.NEUTRAL_70};
          `}
        >
          PLAYERS
        </span>
        <span
          css={css`
            font-family: 'Inter', sans-serif;
            font-size: 14px;
            font-weight: 400;
            line-height: 20.3px;
            color: ${colors.WHITE};
          `}
        >
          {division.playersCount}
        </span>
      </div>

      {/* Actions */}
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;
        `}
        onClick={(e) => e.stopPropagation()}
      >
        {division.isApproved ? (
          // Approved state - show static badge
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              padding: 4px 8px;
              background-color: rgba(16, 173, 50, 0.1);
              border: 1px solid ${colors.SUCCESS_300};
              border-radius: 8px;
            `}
          >
            <span
              css={css`
                font-family: 'Inter', sans-serif;
                font-size: 14px;
                font-weight: 500;
                line-height: 20.3px;
                color: ${colors.SUCCESS_300};
                white-space: nowrap;
              `}
            >
              Approved
            </span>
          </div>
        ) : (
          // Not approved - show controls
          <>
            <button
              type='button'
              css={iconButtonStyles}
              aria-label={`Add team to ${division.name}`}
              onClick={onAddTeam}
            >
              <PlusOutlined
                css={css`
                  color: ${colors.PRIMARY_300};
                `}
              />
            </button>
            <button
              type='button'
              css={iconButtonStyles}
              aria-label={`Delete ${division.name}`}
              onClick={onDelete}
            >
              <DeleteOutlined
                css={css`
                  color: ${colors.ERROR_300};
                `}
              />
            </button>
          </>
        )}
      </div>
    </div>
  )
}

// ============================================
// Division Expanded Content (Teams List)
// ============================================

interface DivisionExpandedContentProps {
  division: DivisionItem
  onDeleteTeam?: (divisionId: string, teamId: string, teamName: string) => void
}

const DivisionExpandedContent: React.FC<DivisionExpandedContentProps> = ({
  division,
  onDeleteTeam,
}) => {
  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        background-color: ${colors.SECONDARY_500};
      `}
    >
      {/* Table Header */}
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 8px 16px 8px 32px;
          background-color: ${colors.SECONDARY_500};
        `}
      >
        <div
          css={css`
            flex: 1;
            max-width: 600px;
          `}
        >
          <span
            css={css`
              font-family: 'Inter', sans-serif;
              font-size: 12px;
              font-weight: 500;
              line-height: 17.4px;
              color: ${colors.NEUTRAL_70};
            `}
          >
            TEAM
          </span>
        </div>
        <span
          css={css`
            font-family: 'Inter', sans-serif;
            font-size: 12px;
            font-weight: 500;
            line-height: 17.4px;
            color: ${colors.NEUTRAL_70};
            width: 80px;
          `}
        >
          PLAYERS
        </span>
        {/* Invisible spacer for alignment with more_vert */}
        <div
          css={css`
            width: 24px;
            opacity: 0;
          `}
        />
      </div>

      {/* Team Rows */}
      {division.teams.length === 0 ? (
        <div
          css={css`
            padding: 16px 32px;
            text-align: center;
            color: ${colors.NEUTRAL_50};
            font-family: 'Inter', sans-serif;
            font-size: 14px;
          `}
        >
          No teams in this division
        </div>
      ) : (
        division.teams.map((team) => (
          <div
            key={team.id}
            css={css`
              display: flex;
              align-items: center;
              justify-content: space-between;
              padding: 8px 16px 8px 32px;
              background-color: ${colors.SECONDARY_500};

              &:hover {
                background-color: ${colors.SECONDARY_400};
              }
            `}
          >
            {/* Team Info */}
            <div
              css={css`
                flex: 1;
                max-width: 600px;
                display: flex;
                align-items: center;
                gap: 8px;
              `}
            >
              {/* Team Logo */}
              <div
                css={css`
                  width: 30px;
                  height: 30px;
                  border-radius: 8px;
                  overflow: hidden;
                  flex-shrink: 0;
                  background-color: ${colors.WHITE};
                `}
              >
                {team.image ? (
                  <img
                    src={team.image}
                    alt={team.name}
                    css={css`
                      width: 100%;
                      height: 100%;
                      object-fit: contain;
                    `}
                  />
                ) : (
                  <div
                    css={css`
                      width: 100%;
                      height: 100%;
                      background-color: ${colors.PRIMARY_300};
                    `}
                  />
                )}
              </div>

              {/* Team Name */}
              <span
                css={css`
                  font-family: 'Inter', sans-serif;
                  font-size: 14px;
                  font-weight: 400;
                  line-height: 20.3px;
                  color: ${colors.WHITE};
                  overflow: hidden;
                  text-overflow: ellipsis;
                  white-space: nowrap;
                `}
              >
                {team.name}
              </span>
            </div>

            {/* Player Count */}
            <span
              css={css`
                font-family: 'Inter', sans-serif;
                font-size: 14px;
                font-weight: 400;
                line-height: 20.3px;
                color: ${colors.WHITE};
                width: 80px;
              `}
            >
              ---
            </span>

            {/* Delete Team */}
            <button
              type='button'
              css={iconButtonStyles}
              aria-label={`Delete ${team.name}`}
              onClick={() => onDeleteTeam?.(division.id, team.id, team.name)}
            >
              <DeleteOutlined
                css={css`
                  color: ${colors.ERROR_300};
                `}
              />
            </button>
          </div>
        ))
      )}
    </div>
  )
}

export default DivisionTeamsSection
